import { useEffect, useRef, useState } from 'react';
import { skip } from 'rxjs';
import { getInventoryChangedSubject } from '../../store/Inventory';
import { getSelectedAccountIds, getSelectedAccountIdsForUrl, getSelectedAccountsSubject } from '../../store/User';
import { accountScopeKey } from '../../utils/accountIds';
import { getLocationDetail, LocationDetail } from './locationsApi';

export type LocationDetailState =
  | { status: 'loading' }
  | { status: 'loaded'; detail: LocationDetail }
  | { status: 'not_found' }
  | { status: 'failed' };

type Answer = Awaited<ReturnType<typeof getLocationDetail>>;

const isNotFound = (error: Answer['error']) => error?.errorResponse?.response?.status === 404;

export const useLocationDetail = (locationKey: string) => {
  const [state, setState] = useState<LocationDetailState>({ status: 'loading' });
  const [scope, setScope] = useState<number>(0);
  const [accountIds, setAccountIds] = useState<string[]>(() => getSelectedAccountIds() ?? []);
  const reads = useRef<number>(0);

  useEffect(() => {
    const accountsSub = getSelectedAccountsSubject()
      .pipe(skip(1))
      .subscribe(() => setScope((count) => count + 1));
    return () => accountsSub.unsubscribe();
  }, []);

  useEffect(() => {
    const read = (onAnswer: (answer: Answer) => void) => {
      const current = ++reads.current;
      getLocationDetail(locationKey, getSelectedAccountIdsForUrl()).then((answer) => {
        if (current === reads.current) onAnswer(answer);
      });
    };

    setAccountIds((shown) => {
      const current = getSelectedAccountIds() ?? [];
      return accountScopeKey(shown) === accountScopeKey(current) ? shown : current;
    });
    setState({ status: 'loading' });
    read(({ data, error }) => {
      if (data) setState({ status: 'loaded', detail: data });
      else setState({ status: isNotFound(error) ? 'not_found' : 'failed' });
    });

    const changesSub = getInventoryChangedSubject().subscribe(() =>
      read(({ data, error }) => {
        if (data) setState({ status: 'loaded', detail: data });
        else if (isNotFound(error)) setState({ status: 'not_found' });
        else setState((shown) => (shown.status === 'loaded' ? shown : { status: 'failed' }));
      })
    );
    return () => {
      changesSub.unsubscribe();
      reads.current += 1;
    };
  }, [locationKey, scope]);

  return { state, scope, accountIds, retry: () => setScope((count) => count + 1) };
};
